import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { ChannelAccountStatus, GmailChannelConfigSchema, WebFormChannelConfigSchema, } from '@wit-support/admin-contracts';
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle, toast, } from '@wit-support/ui';
import { ChannelBadge } from '@wit-support/ui-domain';
import { Actions, SubmitButton, ZodField, ZodForm } from '@wit-support/ui-forms';
import { reconnectGoogle, updateChannel } from '../api.js';
import { useGoogleOAuthPopup } from '../hooks/use-google-oauth-popup.js';
/**
 * Build a per-kind edit schema. Kind is immutable post-create; the form shows
 * a ChannelBadge for it. Config subtree uses the matching kind's schema so
 * labels / validation / secret-flagging all come through.
 */
function buildSchemaForKind(kind) {
    const statusMeta = {
        label: 'Status',
        options: [
            { value: 'active', label: 'Active' },
            { value: 'paused', label: 'Paused' },
            { value: 'reauth_required', label: 'Reauth required' },
            { value: 'error', label: 'Error' },
        ],
    };
    const baseShape = {
        name: z
            .string()
            .min(1, 'Required')
            .max(128, 'Must be 128 characters or fewer')
            .meta({ label: 'Name' }),
        outboundIdentity: z
            .string()
            .email('Enter a valid email address')
            .or(z.literal(''))
            .optional()
            .meta({ label: 'Outbound identity' }),
        status: ChannelAccountStatus.meta(statusMeta),
    };
    if (kind === 'gmail') {
        return z.object({
            ...baseShape,
            config: GmailChannelConfigSchema.meta({ label: 'Configuration' }),
        });
    }
    if (kind === 'web_form') {
        return z.object({
            ...baseShape,
            config: WebFormChannelConfigSchema.meta({ label: 'Configuration' }),
        });
    }
    // Unsupported kinds (imap / smtp_inbound) — edit only top-level fields.
    return z.object(baseShape);
}
export function EditChannelDialog({ channel, onClose, onSaved }) {
    const mut = useMutation({
        mutationFn: (body) => updateChannel(channel.id, body),
        onSuccess: () => {
            toast.success('Channel updated');
            onSaved();
        },
        onError: (err) => toast.error(err.message),
    });
    const schema = buildSchemaForKind(channel.kind);
    // Pre-populate config with the channel's current (possibly redacted) config.
    // pruneUntouchedSecrets will drop untouched secret fields on submit.
    const defaultValues = {
        name: channel.name,
        outboundIdentity: channel.outboundIdentity ?? '',
        status: channel.status,
        config: channel.config ?? {},
    };
    return (_jsx(Dialog, { open: true, onOpenChange: (v) => (!v ? onClose() : null), children: _jsxs(DialogContent, { children: [_jsx(DialogTitle, { children: "Edit channel" }), _jsx(DialogDescription, { children: "Leave secret fields unchanged to preserve existing values." }), _jsxs("div", { className: "mb-3 flex items-center gap-2 text-sm", children: [_jsx("span", { className: "text-[var(--color-text-muted)]", children: "Kind" }), _jsx(ChannelBadge, { kind: channel.kind })] }), channel.kind === 'gmail' ? (_jsx(ReconnectGoogleRow, { channelId: channel.id, status: channel.status, onReconnected: onSaved })) : null, _jsxs(ZodForm, { schema: schema, defaultValues: defaultValues, onSubmit: (values) => {
                        const v = values;
                        const payload = {};
                        if (v.name && v.name !== channel.name)
                            payload.name = v.name;
                        const nextOutbound = v.outboundIdentity === '' || v.outboundIdentity === undefined
                            ? null
                            : v.outboundIdentity;
                        if (nextOutbound !== channel.outboundIdentity) {
                            payload.outboundIdentity = nextOutbound;
                        }
                        if (v.status && v.status !== channel.status)
                            payload.status = v.status;
                        if (v.config && Object.keys(v.config).length > 0) {
                            payload.config = v.config;
                        }
                        if (Object.keys(payload).length === 0) {
                            onClose();
                            return;
                        }
                        mut.mutate(payload);
                    }, children: [_jsx(ZodField, { name: "name" }), _jsx(ZodField, { name: "outboundIdentity" }), _jsx(ZodField, { name: "status" }), channel.kind === 'gmail' || channel.kind === 'web_form' ? (_jsx(ZodField, { name: "config" })) : null, _jsxs(Actions, { children: [_jsx(Button, { type: "button", variant: "ghost", onClick: onClose, children: "Cancel" }), _jsx(SubmitButton, { pendingLabel: "Saving\u2026", children: "Save" })] })] })] }) }));
}
function ReconnectGoogleRow({ channelId, status, onReconnected, }) {
    const { popup, openPopup } = useGoogleOAuthPopup({
        onDone: (viaMessage) => {
            if (viaMessage)
                toast.success('Google reconnected');
            onReconnected();
        },
    });
    const mut = useMutation({
        mutationFn: () => reconnectGoogle(channelId),
        onSuccess: ({ authUrl }) => openPopup(authUrl),
        onError: (err) => toast.error(err.message),
    });
    // The banner is more prominent when the channel is stuck — drives admins to
    // click it rather than leaving the row in a broken state.
    const needsAttention = status === 'reauth_required' || status === 'pending_oauth';
    return (_jsxs("div", { className: needsAttention
            ? 'mb-3 flex items-center justify-between gap-3 rounded-[var(--radius-3)] border border-[var(--color-border-default)] bg-[var(--color-surface-sunken)] px-3 py-2 text-sm'
            : 'mb-3 flex items-center justify-between gap-3 text-sm', children: [_jsxs("div", { className: "min-w-0", children: [_jsx("p", { className: "font-medium text-[var(--color-text-primary)]", children: "Google sign-in" }), _jsx("p", { className: "text-xs text-[var(--color-text-muted)]", children: status === 'reauth_required'
                            ? 'Refresh token revoked — reconnect to restore delivery.'
                            : status === 'pending_oauth'
                                ? 'Waiting for Google consent.'
                                : 'Reconnect to rotate the refresh token or switch mailbox.' })] }), _jsx(Button, { type: "button", variant: needsAttention ? 'primary' : 'secondary', size: "sm", onClick: () => mut.mutate(), disabled: mut.isPending, children: mut.isPending ? 'Opening Google…' : popup && !popup.closed ? 'Waiting…' : 'Reconnect' })] }));
}
//# sourceMappingURL=edit-dialog.js.map